/**
 * PlatenAssignmentSelects: the ONE platen plus platen variant picker pair.
 *
 * Every surface that assigns a size to a platen variant renders this: the
 * Settings "Platen size" rows, the product sync missing-size prompt and the
 * per-product Advanced overrides. Options come ONLY from the shared closed
 * lists (`DTG_PLATEN_CODES`, `PLATEN_VARIANTS`), so a new platen or variant
 * is a one-line change in `rip-routing.ts` and reaches every picker at once.
 *
 * Stateless and rule-agnostic: the caller owns the value, decides whether the
 * pair is `invalid` (an empty side gets the amber border) and which side has
 * `changed` since the last save (the purple dirty border). Placeholders and
 * aria-labels come from the shared `common:platenSelect.*` keys so every
 * surface reads the same.
 *
 * `layout="cells"` renders the two Selects as two `<td>`s for a table with a
 * Platen and a Platen variant column; the default renders them side by side.
 *
 * `mode="combined"` renders ONE Select whose options are every platen variant
 * ("14x16 Small", built from the same two closed lists). Use it where each
 * pick is saved at once (the per-product Advanced overrides): a pair picker
 * there writes a half-changed pair ("10x12 Large" on the way from 14x16 Large
 * to 10x12 Small), so the size jumps groups twice. Combined, one pick is one
 * complete write. In `cells` layout it renders ONE `<td>`.
 *
 * Widths are sized for the placeholders ("Choose platen", "选择压板规格"),
 * which are the longest thing a trigger shows: the platen code is set in mono
 * only once one is chosen (a mono placeholder clips), and the compact pair
 * uses `text-xs`. Variant options show the translated name ("Small" / "小号");
 * the Select VALUE stays the code.
 */
import { useTranslation } from 'react-i18next';
import {
  DTG_PLATEN_CODES,
  PLATEN_VARIANTS,
  type DtgPlatenCode,
  type PlatenVariant,
} from '@threadheads/shared';
import { cn } from '@/lib/utils';
import { platenGroupLabel, platenVariantLabel } from '@/lib/platen-size-range';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/** A pair that may still be half chosen (a newly added size row). */
export interface PlatenAssignmentDraft {
  platen: DtgPlatenCode | null;
  platenVariant: PlatenVariant | null;
}

export interface PlatenAssignmentSelectsProps {
  value: PlatenAssignmentDraft;
  onChange: (next: PlatenAssignmentDraft) => void;
  /** The size code, for the aria-labels ("Platen for XL in TEE"). */
  sizeLabel: string;
  /** The product type code, for the aria-labels. */
  productTypeLabel: string;
  disabled?: boolean;
  /** Amber border on every EMPTY side (a required pair that is not complete). */
  invalid?: boolean;
  /** Purple border on the side(s) changed since the last save. */
  changed?: { platen?: boolean; platenVariant?: boolean };
  /** Id of the line that explains the invalid state, for aria-describedby. */
  describedBy?: string;
  layout?: 'inline' | 'cells';
  /** `pair` (default): two Selects. `combined`: one Select of every platen variant. */
  mode?: 'pair' | 'combined';
  /** `compact` is the `h-8 w-32 text-xs` pair for dense product sync rows
   *  (the missing-size prompt and the Advanced table); default is the
   *  Settings `h-11 w-36` pair. */
  size?: 'default' | 'compact';
  /** `combined` mode only: the Select's aria-label in place of the per-size
   *  one, for a picker that is not about one size (the Settings bulk tool). */
  ariaLabel?: string;
  className?: string;
}

const isDtgPlatenCode = (v: string): v is DtgPlatenCode =>
  (DTG_PLATEN_CODES as readonly string[]).includes(v);
const isPlatenVariant = (v: string): v is PlatenVariant =>
  (PLATEN_VARIANTS as readonly string[]).includes(v);

const TRIGGER_MOTION = 'motion-safe:transition-colors motion-safe:duration-200 motion-reduce:transition-none';
const TRIGGER_SIZE = { default: 'h-11 w-36', compact: 'h-8 w-32 text-xs' } as const;
const COMBINED_TRIGGER_SIZE = { default: 'h-11 w-44', compact: 'h-8 w-40 text-xs' } as const;

/** Every platen variant, in the shared list order (platen first, then variant). */
const COMBINED_OPTIONS = DTG_PLATEN_CODES.flatMap((platen) =>
  PLATEN_VARIANTS.map((platenVariant) => ({ platen, platenVariant, value: `${platen}|${platenVariant}` })),
);

function borderFor(empty: boolean, invalid: boolean, changed: boolean): string | undefined {
  // The loudest true thing owns the border: unset first, then dirty.
  if (invalid && empty) return 'border-amber-300';
  if (changed) return 'border-threadheads-purple';
  return undefined;
}

export function PlatenAssignmentSelects({
  value,
  onChange,
  sizeLabel,
  productTypeLabel,
  disabled = false,
  invalid = false,
  changed,
  describedBy,
  layout = 'inline',
  mode = 'pair',
  size = 'default',
  ariaLabel,
  className,
}: PlatenAssignmentSelectsProps): JSX.Element {
  const triggerBase = cn(TRIGGER_SIZE[size], TRIGGER_MOTION);
  const { t } = useTranslation('common');
  const labelVars = { size: sizeLabel, productType: productTypeLabel };
  const platenEmpty = value.platen === null;
  const variantEmpty = value.platenVariant === null;

  if (mode === 'combined') {
    const current = value.platen && value.platenVariant ? `${value.platen}|${value.platenVariant}` : '';
    const empty = current === '';
    const combinedSelect = (
      <Select
        value={current}
        disabled={disabled}
        onValueChange={(v) => {
          const option = COMBINED_OPTIONS.find((o) => o.value === v);
          if (option) onChange({ platen: option.platen, platenVariant: option.platenVariant });
        }}
      >
        <SelectTrigger
          className={cn(
            COMBINED_TRIGGER_SIZE[size],
            TRIGGER_MOTION,
            'tabular-nums',
            borderFor(empty, invalid, !!(changed?.platen || changed?.platenVariant)),
          )}
          aria-label={ariaLabel ?? t('common:platenSelect.combinedAria', labelVars)}
          aria-invalid={(invalid && empty) || undefined}
          aria-describedby={invalid && empty ? describedBy : undefined}
        >
          <SelectValue placeholder={t('common:platenSelect.combinedPlaceholder')} />
        </SelectTrigger>
        <SelectContent>
          {COMBINED_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value} className="tabular-nums">
              {platenGroupLabel(t, option)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
    return layout === 'cells' ? (
      <td className={cn('px-3 py-2', className)}>{combinedSelect}</td>
    ) : (
      <div className={cn('flex items-center', className)}>{combinedSelect}</div>
    );
  }

  const platenSelect = (
    <Select
      value={value.platen ?? ''}
      disabled={disabled}
      onValueChange={(v) => {
        if (isDtgPlatenCode(v)) onChange({ ...value, platen: v });
      }}
    >
      <SelectTrigger
        className={cn(triggerBase, !platenEmpty && 'font-mono', borderFor(platenEmpty, invalid, !!changed?.platen))}
        aria-label={t('common:platenSelect.platenAria', labelVars)}
        aria-invalid={(invalid && platenEmpty) || undefined}
        aria-describedby={invalid && platenEmpty ? describedBy : undefined}
      >
        <SelectValue placeholder={t('common:platenSelect.platenPlaceholder')} />
      </SelectTrigger>
      <SelectContent>
        {DTG_PLATEN_CODES.map((code) => (
          <SelectItem key={code} value={code} className="font-mono">
            {code}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const variantSelect = (
    <Select
      value={value.platenVariant ?? ''}
      disabled={disabled}
      onValueChange={(v) => {
        if (isPlatenVariant(v)) onChange({ ...value, platenVariant: v });
      }}
    >
      <SelectTrigger
        className={cn(triggerBase, borderFor(variantEmpty, invalid, !!changed?.platenVariant))}
        aria-label={t('common:platenSelect.variantAria', labelVars)}
        aria-invalid={(invalid && variantEmpty) || undefined}
        aria-describedby={invalid && variantEmpty ? describedBy : undefined}
      >
        <SelectValue placeholder={t('common:platenSelect.variantPlaceholder')} />
      </SelectTrigger>
      <SelectContent>
        {PLATEN_VARIANTS.map((variant) => (
          <SelectItem key={variant} value={variant}>
            {platenVariantLabel(t, variant)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  if (layout === 'cells') {
    return (
      <>
        <td className={cn('px-3 py-2', className)}>{platenSelect}</td>
        <td className={cn('px-3 py-2', className)}>{variantSelect}</td>
      </>
    );
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {platenSelect}
      {variantSelect}
    </div>
  );
}
